import React from 'react';
import { motion } from 'framer-motion';
import { Heart, Leaf, Sparkles, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";

const AboutPage = () => {
  const values = [
    {
      icon: Heart,
      title: 'Handcrafted with Love',
      description: 'Every candle is hand-poured with care and attention to detail, ensuring the highest quality for your home.'
    },
    {
      icon: Leaf,
      title: 'Eco-Conscious',
      description: 'We use sustainable soy wax, recyclable packaging, and eco-friendly practices in everything we do.'
    },
    {
      icon: Sparkles,
      title: 'Premium Fragrances',
      description: 'Our unique scent blends are carefully crafted to create the perfect ambiance in any space.'
    },
    {
      icon: MapPin,
      title: 'Made in Scotland',
      description: 'Proudly crafted in the heart of Scotland, inspired by the natural beauty of the Highlands.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="about-page">
      {/* Hero */}
      <section className="relative min-h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1765707659072-dcec2ae39457?crop=entropy&cs=srgb&fm=jpg&q=85"
            alt="Candle Making"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>

        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-cursive text-2xl md:text-3xl text-[#E5C88A] mb-4"
          >
            Our Story
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-6"
          >
            About Glow & Gather
          </motion.h1>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-cursive text-2xl text-[#C5A059] mb-4">Welcome</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C2C2C] mb-8">
              A Passion for Light & Warmth
            </h2>
            <div className="text-[#595959] leading-relaxed space-y-6 text-lg">
              <p>
                Glow & Gather began in a small kitchen in Scotland, born from a love of cosy evenings, 
                beautiful scents, and the simple magic of candlelight. What started as a hobby quickly 
                grew into a passion for creating handcrafted candles that bring warmth and tranquility 
                to homes across the UK.
              </p>
              <p>
                Each of our candles is lovingly hand-poured using premium soy wax and carefully selected 
                fragrance oils. We believe that the flicker of a candle flame has the power to transform 
                any moment into something special – whether you're unwinding after a long day, hosting 
                friends, or simply enjoying a quiet cup of tea.
              </p>
              <p>
                Inspired by the rugged beauty of the Scottish Highlands, our scents capture the essence 
                of heather-covered hills, cosy firesides, and fresh morning mists. We're proud to create 
                products that not only smell incredible but are also kind to our planet.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 px-6 md:px-12 bg-[#F5F1EB] grain-texture">
        <div className="max-w-6xl mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="font-cursive text-2xl text-[#C5A059] mb-2">What We Believe</p>
            <h2 className="font-serif text-3xl md:text-4xl text-[#2C2C2C]">Our Values</h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 text-center shadow-sm border border-stone-100"
              >
                <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-[#C5A059]/10 flex items-center justify-center">
                  <value.icon className="w-8 h-8 text-[#C5A059]" />
                </div>
                <h3 className="font-serif text-xl text-[#2C2C2C] mb-3">{value.title}</h3>
                <p className="text-[#595959] text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Sarah Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <p className="font-cursive text-2xl text-[#C5A059] mb-4">The Maker</p>
              <h2 className="font-serif text-3xl md:text-4xl text-[#2C2C2C] mb-6">
                Meet Sarah
              </h2>
              <div className="text-[#595959] leading-relaxed space-y-4">
                <p>
                  Hello! I'm Sarah, the heart and hands behind Glow & Gather. My journey into 
                  candle making began when I was searching for a way to bring more calm and 
                  comfort into my own home.
                </p>
                <p>
                  Unable to find candles with the perfect blend of quality, sustainability, 
                  and beautiful scents, I decided to create my own. What started at my kitchen 
                  table has grown into something I'm incredibly proud of.
                </p>
                <p>
                  Every candle I create is made with love, and I hope they bring as much joy 
                  to your home as they do to mine. Thank you for supporting small, Scottish business!
                </p>
              </div>
              <p className="font-cursive text-xl text-[#C5A059] mt-6">Sarah x</p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="order-1 lg:order-2"
            >
              <div className="aspect-square rounded-2xl overflow-hidden bg-[#F5F1EB]">
                <img
                  src="https://images.unsplash.com/photo-1664205974904-91569e75dd64?crop=entropy&cs=srgb&fm=jpg&q=85"
                  alt="Handcrafted Candles"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 md:px-12 bg-[#2C2C2C]">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="font-cursive text-2xl text-[#E5C88A] mb-4">Ready to Experience</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-6">
              The Glow & Gather Difference?
            </h2>
            <p className="text-stone-400 mb-8">
              Explore our collection of handcrafted candles and bring the warmth of Scotland into your home.
            </p>
            <Link to="/shop">
              <Button className="btn-primary text-lg px-10 py-6 gold-glow">
                Shop Our Collection
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
